import { type GridLocaleText } from '@mui/x-data-grid'
import { deDE, enUS, esES, frFR, itIT, jaJP, nlNL, ptPT, zhCN } from '@mui/x-data-grid/locales'
import { AvailableLocale } from '@omnea-digital/toolbox'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTranslationsContext } from '@omnea-app/fe-i18n/TranslationsContext'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'

const getMuiTranslationsByLocale = (locale: AvailableLocale): Partial<GridLocaleText> => {
  switch (locale) {
    case 'en-GB': // MUI has only American english
    case 'en-US':
      return enUS.components.MuiDataGrid.defaultProps.localeText

    case 'fr-FR':
      return frFR.components.MuiDataGrid.defaultProps.localeText

    case 'es-ES':
      return esES.components.MuiDataGrid.defaultProps.localeText

    case 'it-IT':
      return itIT.components.MuiDataGrid.defaultProps.localeText

    case 'de-DE':
      return deDE.components.MuiDataGrid.defaultProps.localeText

    case 'ja-JP':
      return jaJP.components.MuiDataGrid.defaultProps.localeText

    case 'nl-NL':
      return nlNL.components.MuiDataGrid.defaultProps.localeText

    case 'pt-PT':
      return ptPT.components.MuiDataGrid.defaultProps.localeText

    case 'zh-CN':
      return zhCN.components.MuiDataGrid.defaultProps.localeText

    default:
      assertNever(locale)
      return getMuiTranslationsByLocale('en-US')
  }
}

export const useMuiDataGridLocaleText = (): Partial<GridLocaleText> => {
  const { locale } = useTranslationsContext()
  const { t } = useTranslation('common')

  return useMemo(
    () => ({
      ...getMuiTranslationsByLocale(locale),
      toolbarColumns: t('table.table-toolbar.columns-button-label'),
      toolbarExport: t('table.table-toolbar.export-button-label'),

      // These were already overridden before the localization work
      // Keeping the override for all languages
      toolbarFilters: '',
      toolbarDensity: '',
    }),
    [locale, t]
  )
}
